import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { AuditTrailPanel } from '../../../components/resource-detail/audit-trail-panel.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { useSoftDeletePmsCredential } from '../api/mutations.js';
import { usePmsCredential } from '../api/queries.js';

function yesNo(value: boolean): string {
  return value ? 'Yes' : 'No';
}

export function PmsCredentialDetailPage(): React.JSX.Element {
  const pmsCredentialId = useParams().pmsCredentialId ?? '';
  const navigate = useNavigate();
  const query = usePmsCredential(pmsCredentialId);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const softDelete = useSoftDeletePmsCredential();

  const onDelete = async (): Promise<void> => {
    await softDelete.mutateAsync(pmsCredentialId);
    setConfirmingDelete(false);
    void navigate('/pms-credentials');
  };

  return (
    <ResourceDetailPage
      query={query}
      permission={PERMISSIONS.PMS_READ}
      parent={{ to: '/pms-credentials', label: 'PMS credentials' }}
      title={(data) => data.name}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.PMS_CONFIGURE}>
            <Button asChild variant="outline">
              <Link to={`/pms-credentials/${pmsCredentialId}/edit`}>Edit</Link>
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.PMS_CONFIGURE}>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmingDelete(true);
              }}
            >
              Delete
            </Button>
          </PermissionGuard>
        </>
      }
    >
      {(data) => (
        <>
          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title="Delete PMS credential"
            description={`${data.name} will be removed from the list. Its history is retained, not erased.`}
            confirmLabel="Delete"
            destructive
            loading={softDelete.isPending}
            onConfirm={() => {
              void onDelete();
            }}
          />

          <DetailGrid>
            <DetailField label="Connection name">{data.name}</DetailField>
            <DetailField label="PMS type">{data.pmsProvider}</DetailField>
            <DetailField label="Status">
              <StatusBadge tone={toneForStatus(data.status)}>{data.status}</StatusBadge>
            </DetailField>
            <DetailField label="Base URL">{data.baseUrl}</DetailField>
            <DetailField label="Timezone">{data.timezone}</DetailField>
            <DetailField label="Use test environment">{yesNo(data.useTestEnvironment)}</DetailField>

            <DetailField label="Agent ID (Auth)">{data.agentId}</DetailField>
            <DetailField label="Agent secret">{data.agentSecret}</DetailField>
            <DetailField label="Client ID">{data.clientId}</DetailField>
            <DetailField label="Client secret">{data.clientSecret}</DetailField>

            <DetailField label="Travel agent ID">{data.travelAgentId}</DetailField>
            <DetailField label="Market segment ID">{data.marketSegmentId}</DetailField>
            <DetailField label="Booking source ID">{data.bookingSourceId}</DetailField>
            <DetailField label="Partial reservation expire time">
              {data.partialReservationExpireTime}
            </DetailField>

            <DetailField label="Agent ID (Rate Grid)">{data.rateGridAgentId}</DetailField>
            <DetailField label="Travel agent ID (Master Rates)">
              {data.masterRateTravelAgentId}
            </DetailField>
            <DetailField label="Travel agent ID (Reservations)">
              {data.reservationTravelAgentId}
            </DetailField>

            <DetailField label="Module types">{data.moduleType.join(', ')}</DetailField>
            <DetailField label="Version">{String(data.version)}</DetailField>
          </DetailGrid>
          <AuditTrailPanel entityType="pmsCredential" entityId={data.id} />
        </>
      )}
    </ResourceDetailPage>
  );
}
